<template>
  <v-card outlined>
    <v-container class="px-1 py-1" fluid>
      <v-card-title class="pt-2 pb-0">
        {{ $t('ELE0028') }}
      </v-card-title>
      <v-card-text>
        <validation-observer ref="observer" v-slot="{ invalid }">
          <v-row dense class="py-2">
            <v-col>
              <validation-provider v-slot="{ errors }" name="IQC Company" rules="required">
                <AcceptCompanyInput
                  :label="$t('ELE0127')"
                  v-model="searchCondition.companyCd"
                  :error-messages="errors"
                  required
                />
              </validation-provider>
            </v-col>
            <v-col>
              <PartCodeInput v-if="isEpson" v-model="searchCondition.partCd" />
              <SupplierPartInput v-else v-model="searchCondition.partCd" :supplierCd="''" isSearchByLoginCompany />
            </v-col>
            <v-col>
              <ManufacturerInput :label="$t('ELE0134')" v-model="searchCondition.supplierCd" />
            </v-col>
          </v-row>
          <v-row class="px-1">
            <v-col cols="12" md="9" class="py-2">
              <v-row dense>
                <TextFieldInputWithDatePicker :label="$t('ELE0128')" v-model="searchCondition.fromDate" />
                <span class="px-4 py-2"> {{ $t('ELE0141') }} </span>
                <TextFieldInputWithDatePicker :label="$t('ELE0129')" v-model="searchCondition.toDate" />
              </v-row>
            </v-col>
            <v-col cols="12" md="3">
              <v-row justify="end">
                <v-card-text>
                  <BtnSearch @click="onClickSearch" :disabled="invalid" />
                </v-card-text>
              </v-row>
            </v-col>
          </v-row>
        </validation-observer>
        <v-row class="pt-2" v-if="displayResultComputed">
          <v-col>
            <v-card outlined>
              <v-card-title class="pt-2 pb-2">
                {{ $t('ELE0023') }}
                <v-spacer />
                <Btn :label="$t('ELE0325')" @click="onPrintClick" :disabled="!displayResultComputed" />
                <BtnCsvDownload
                  class="mx-2"
                  :label="$t('ELE0040')"
                  fileTitle="defect_rate"
                  :items="[]"
                  :itemParsed="csvOutputDataComputed"
                />
              </v-card-title>
              <v-data-table
                :headers="headers"
                :items="rateObjectListComputed"
                disable-pagination
                hide-default-footer
                dense
              >
                <template v-slot:header>
                  <thead>
                    <tr>
                      <th class="top-headers border-top" colspan="2"></th>
                      <th
                        v-for="(item, index) in weekHeaderComputed"
                        v-bind:key="index"
                        :class="`${
                          index !== weekHeaderComputed.length - 1 ? 'top-headers' : ''
                        } border-top text-center`"
                        :colspan="item.days"
                      >
                        {{ item.name }}
                      </th>
                    </tr>
                  </thead>
                </template>
                <!-- <template v-slot:item.main="{ item }">
                  <td :class="`${item.class + ' v-data-table__divider'}`">{{ item.main }}</td>
                </template> -->
                <td
                  v-for="(header, index) in headers"
                  :slot="`item.${header.value}`"
                  slot-scope="{ item }"
                  v-bind:key="index"
                  :class="`${item[header.class]} ${item[header.cellClass]} text-${header.align} ${
                    header.divider ? 'v-data-table__divider' : ''
                  }`"
                >
                  <a
                    v-if="item[header.cellClass] && item[header.value] >= 0"
                    @click="onNgLotCLick(header)"
                    @href.stop.prevent=""
                    style="text-decoration: underline"
                  >
                    {{ item[header.value] }}
                  </a>
                  <div v-else>{{ item[header.value] }}</div>
                </td>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-if="showDefectList">
          <v-col>
            <v-card outlined>
              <v-card-title class="pt-1 pb-0">
                {{ $t('ELE0321') }}
                <v-spacer />
              </v-card-title>
              <v-data-table
                :headers="defectListHeader"
                :items="defectListComputed"
                disable-pagination
                hide-default-footer
                dense
              >
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-container>
  </v-card>
</template>

<script>
import { API } from '@/plugins/api'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import DateUtils from '@/mixin/date-utils.js'
import StringUtils from '@/mixin/string-utils.js'
import CompanyInput from '@/components/atoms/select/CompanyInput.vue'
import PartCodeInput from '@/components/atoms/select/PartCodeInput.vue'
import SupplierPartInput from '@/components/atoms/select/SupplierPartInput'
import BtnSearch from '@/components/atoms/button/BtnSearch.vue'
import Btn from '@/components/atoms/base/button/Btn'
import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker.vue'
import moment from 'moment'
import cloneDeep from 'lodash/cloneDeep'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import AcceptCompanyInput from '@/components/atoms/select/AcceptCompanyInput.vue'
import ManufacturerInput from '@/components/atoms/select/ManufacturerInput'
import BtnCsvDownload from '@/components/atoms/button/BtnCsvDownload'
import { mapGetters } from 'vuex'

export default {
  name: 'DefectRateForm',

  components: {
    CompanyInput,
    PartCodeInput,
    SupplierPartInput,
    BtnSearch,
    Btn,
    TextFieldInputWithDatePicker,
    ValidationObserver,
    ValidationProvider,
    AcceptCompanyInput,
    ManufacturerInput,
    BtnCsvDownload,
  },

  mixins: [APIUtils, Message, Loading, DateUtils, StringUtils],

  data() {
    return {
      searchCondition: {
        companyCd: '',
        partCd: '',
        supplierCd: '',
        fromDate: moment().day(-7).format('YYYY-MM-DD'),
        toDate: moment().day(6).format('YYYY-MM-DD'),
      },
      ppmUnit: 1000000,
      headers: [
        {
          text: '',
          value: 'main',
          width: '200',
          class: 'class',
          divider: true,
          sortable: false,
        },
        {
          text: this.$t('ELE0318'),
          value: 'information',
          width: '200',
          class: '',
          divider: true,
          sortable: false,
        },
      ],
      defectListHeader: [
        { text: this.$t('ELE0049'), value: 'lotNo', divider: true },
        { text: this.$t('ELE0224'), value: 'model', divider: true },
        { text: this.$t('ELE0031'), value: 'partCd', divider: true },
        { text: this.$t('ELE0032'), value: 'partName', divider: true },
        { text: this.$t('ELE0218'), value: 'status', divider: true },
        { text: this.$t('ELE0308'), value: 'defectTotal', divider: true, align: 'right' },
        { text: this.$t('ELE0287'), value: 'partCatName', divider: true },
        { text: this.$t('ELE0281'), value: 'supplierCd', divider: true },
        { text: this.$t('ELE0282'), value: 'supplierName', divider: true },
        { text: this.$t('ELE0012'), value: 'defectCd', divider: true },
        { text: this.$t('ELE0013'), value: 'defectName', divider: true },
        { text: this.$t('ELE0009'), value: 'defectGroupCd', divider: true },
        { text: this.$t('ELE0309'), value: 'defectCount', divider: true, align: 'right' },
        { text: this.$t('ELE0310'), value: 'aqlSampleSize', divider: true, align: 'right' },
      ],
      showDefectList: false,
      defectList: [],
      weekHeader: [],
      rateObjectList: {
        totalReceivedLot: { main: this.$t('ELE0311'), information: '', class: '', anchor: false },
        totalInspLot: {
          main: this.$t('ELE0312'),
          information: this.$t('ELE0319'),
          class: 'border-b-none',
          anchor: false,
        },
        totalAppLot: { main: '', information: this.$t('ELE0313'), class: 'border-b-none', anchor: false },
        totalDimLot: { main: '', information: this.$t('ELE0159'), class: '', anchor: false },
        ngAppLot: { main: this.$t('ELE0314'), information: this.$t('ELE0158'), class: 'border-b-none', anchor: true },
        ngDimLot: { main: '', information: this.$t('ELE0159'), class: '', anchor: true },
        defectAppRate: {
          main: this.$t('ELE0315'),
          information: this.$t('ELE0158'),
          class: 'border-b-none',
          anchor: false,
        },
        defectDimRate: { main: '', information: this.$t('ELE0159'), class: 'border-b-none', anchor: false },
        defectIQCRate: { main: '', information: this.$t('ELE0073'), class: 'border-b-none', anchor: false },
        defectTotalRate: { main: '', information: this.$t('ELE0319'), class: '', anchor: false },
        iqcCheckRate: { main: this.$t('ELE0316'), information: '', class: '', anchor: false },
        weekDefectRate: { main: this.$t('ELE0317'), information: this.$t('ELE0320'), class: '', anchor: false },
      },
      //CSV data
      csvTopHeader: '', //rows containing "week names"
      csvMainHeader: '', //row's main header with "dates"
      dateHeaderArray: [], //list of dates 日付の一覧
      //Keys of row's first two columns in array
      rowInitialsArray: ['main', 'information'], //length must be equal to csvMainHeader length when initialized
    }
  },
  computed: {
    ...mapGetters(['isEpson']),

    displayResultComputed: {
      get() {
        return this.weekHeader.length > 0
      },
    },
    weekHeaderComputed: {
      get() {
        return this.weekHeader
      },
      set(week) {
        this.weekHeader = week.map((item) => {
          return {
            days: item.days,
            name: `${this.getNumberSuffix(item.name)} Week`,
          }
        })
      },
    },
    headersComputed: {
      get() {
        return this.headers
      },
      set(header) {
        this.headers = cloneDeep(header)
      },
    },
    defectListComputed: {
      get() {
        return this.defectList
      },
      set(list) {
        this.showDefectList = true
        this.defectList = cloneDeep(list)
      },
    },
    rateObjectListComputed: {
      get() {
        const val = Object.entries(this.rateObjectList).map(([key, value]) => ({
          name: key,
          ...value,
        }))
        return val
      },
      set(value) {
        this.rateObjectList = cloneDeep(value)
      },
    },
    csvOutputDataComputed: {
      get() {
        //add top header, main header and data
        return (
          this.csvTopHeader +
          this.csvMainHeader +
          this.convertToCSV(this.rateObjectListComputed, this.dateHeaderArray, this.rowInitialsArray)
        )
      },
    },
  },
  methods: {
    initCSVData() {
      this.csvTopHeader = ',,'
      this.csvMainHeader = ',' + this.$t('ELE0318') + ','
      this.dateHeaderArray = []
    },

    onPrintClick() {
      const props = {
        company: this.searchCondition.companyCd,
        part: this.searchCondition.partCd,
        supplier: this.searchCondition.supplierCd,
        dateFrom: this.searchCondition.fromDate,
        dateTo: this.searchCondition.toDate,
        rateListHeader: this.headers,
        rateList: this.rateObjectListComputed,
        weekHeader: this.weekHeader,
        defectListHeader: this.defectListHeader,
        defectList: this.defectList,
      }
      this.$router.push({ name: 'DefectRatePageReport', params: props })
    },
    async onNgLotCLick(item) {
      this.startLoading()
      try {
        const params = {
          ...this.searchCondition,
          date: item.value,
        }
        const response = await API.get('/totalling/ng-lot-detail', params)
        if (response) {
          this.defectListComputed = response
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0314') })))
      } finally {
        this.stopLoading()
      }
    },
    resetTable() {
      this.headersComputed = this.headers.splice(0, 2)
      const validKeys = ['main', 'information', 'class', 'anchor']
      Object.values(this.rateObjectList).map((element) => {
        Object.keys(element).forEach((key) => validKeys.includes(key) || delete element[key])
      })
    },
    calculateAppDimDefectRate(ngLot, totalInspLot) {
      if (totalInspLot == '-') return '-'
      return totalInspLot > 0 ? ((ngLot / totalInspLot) * this.ppmUnit).toFixed(0) : 0
    },
    calculateIQCorTotalDefectRate(ngAppLot, ngDimLot, totalLot) {
      if (totalLot == '-') return '-'
      return totalLot > 0 ? (((ngAppLot + ngDimLot) / totalLot) * this.ppmUnit).toFixed(0) : 0
    },
    calculateIQCCheckRate(totalInspLot, totalReceivedLot) {
      if (totalReceivedLot == '-') return '-'
      return totalReceivedLot > 0 ? ((totalInspLot / totalReceivedLot) * 100).toFixed(2) + '%' : 0 + '%'
    },
    calculateWeekDefectRate(ngAppLotWeekSum, ngDimLotWeekSum, inspLotWeekSum) {
      return inspLotWeekSum > 0 ? (((ngAppLotWeekSum + ngDimLotWeekSum) / inspLotWeekSum) * this.ppmUnit).toFixed(0) : 0
    },
    calculateWeekLastDay(count, dateValue, isLastCol) {
      let ngAppLotWeekSum = 0
      let ngDimLotWeekSum = 0
      let inspLotWeekSum = 0
      for (let i = count; i >= 0; i--) {
        const dateValueColumnText = moment(dateValue).subtract(i, 'd').format('YYYY-MM-DD')
        ngAppLotWeekSum =
          this.rateObjectList.ngAppLot[`${dateValueColumnText}`] >= 0
            ? ngAppLotWeekSum + this.rateObjectList.ngAppLot[`${dateValueColumnText}`]
            : ngAppLotWeekSum
        ngDimLotWeekSum =
          this.rateObjectList.ngDimLot[`${dateValueColumnText}`] >= 0
            ? ngDimLotWeekSum + this.rateObjectList.ngDimLot[`${dateValueColumnText}`]
            : ngDimLotWeekSum
        inspLotWeekSum =
          this.rateObjectList.totalInspLot[`${dateValueColumnText}`] >= 0
            ? inspLotWeekSum + this.rateObjectList.totalInspLot[`${dateValueColumnText}`]
            : inspLotWeekSum
      }
      this.rateObjectList.weekDefectRate[
        isLastCol ? `${dateValue}` : `${moment(dateValue).subtract(1, 'd').format('YYYY-MM-DD')}`
      ] = this.calculateWeekDefectRate(ngAppLotWeekSum, ngDimLotWeekSum, inspLotWeekSum)
    },
    async onClickSearch() {
      this.resetTable()
      this.startLoading()
      try {
        if (moment(this.searchCondition.fromDate).add(35, 'd') <= moment(this.searchCondition.toDate)) {
          this.setErrorMessage(this.$t('MSG0096', { p0: '35' }))
          return
        }
        // TODO: Remove this POST api after auto register into batch tables are applied
        // 自動でバッチに登録する機能が作って終わった後下のAPIを消す
        // const resp = await API.post('/totalling/save-defect-rate', this.searchCondition)
        // console.log(resp)
        const calendarList = await API.get('/master/calendar', this.searchCondition)
        const response = await API.get('/totalling/defect-rate', this.searchCondition)
        this.showDefectList = false
        if (response) {
          this.initCSVData()
          this.weekHeader = []
          let week = []
          let count = 0
          response.forEach((element, index) => {
            const { date, totalReceivedLot, totalInspLot, totalAppLot, totalDimLot, ngAppLot, ngDimLot } = element
            let dateValue = moment(date).format('YYYY-MM-DD')
            this.headers.push({
              text: moment(date).format('Do MMM'),
              value: dateValue,
              cellClass: 'anchor',
              divider: response.length - 1 !== index,
              sortable: false,
              align: 'right',
            })
            this.csvMainHeader = this.csvMainHeader + moment(date).format('Do MMM') + ','
            this.dateHeaderArray.push(date)
            // const ppmUnit = 1000000
            this.rateObjectList.totalReceivedLot[`${dateValue}`] = totalReceivedLot
            this.rateObjectList.totalInspLot[`${dateValue}`] = totalInspLot
            this.rateObjectList.totalAppLot[`${dateValue}`] = totalAppLot
            this.rateObjectList.totalDimLot[`${dateValue}`] = totalDimLot
            this.rateObjectList.ngAppLot[`${dateValue}`] = ngAppLot
            this.rateObjectList.ngDimLot[`${dateValue}`] = ngDimLot
            this.rateObjectList.defectAppRate[`${dateValue}`] = this.calculateAppDimDefectRate(ngAppLot, totalInspLot)
            this.rateObjectList.defectDimRate[`${dateValue}`] = this.calculateAppDimDefectRate(ngDimLot, totalInspLot)
            this.rateObjectList.defectIQCRate[`${dateValue}`] = this.calculateIQCorTotalDefectRate(
              ngAppLot,
              ngDimLot,
              totalInspLot,
            )
            this.rateObjectList.defectTotalRate[`${dateValue}`] = this.calculateIQCorTotalDefectRate(
              ngAppLot,
              ngDimLot,
              totalReceivedLot,
            )
            this.rateObjectList.iqcCheckRate[`${dateValue}`] = this.calculateIQCCheckRate(
              totalInspLot,
              totalReceivedLot,
            )

            const weekMonth = calendarList.find((item) => item.date == dateValue).weekCd
            if (weekMonth != week[week.length - 1]?.name) {
              this.calculateWeekLastDay(count, dateValue, false)
              count = 0
              this.csvTopHeader = this.csvTopHeader + `${this.getNumberSuffix(weekMonth)} Week,`
            } else {
              this.csvTopHeader = this.csvTopHeader + ','
            }
            if (response.length - 1 == index) {
              this.calculateWeekLastDay(count, dateValue, true)
            }
            if (count == 0) {
              week.push({
                days: count + 1,
                name: weekMonth,
              })
            } else {
              week[week.length - 1] = {
                days: count + 1,
                name: weekMonth,
              }
            }
            count++
          })
          this.rateObjectListComputed = this.rateObjectList
          this.weekHeaderComputed = week
          this.csvTopHeader = this.csvTopHeader + '\n'
          this.csvMainHeader = this.csvMainHeader + '\n'
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0318') })))
      } finally {
        this.stopLoading()
      }
    },
  },
  mounted() {
    this.initCSVData()
  },
}
</script>

<style>
.border-b-none {
  border-bottom: none !important;
}
</style>
